/**
 * How a case's input and expectations are edited. Each suite has its own shapes, so each
 * gets its own form; a suite without one is edited as JSON. The API checks the result
 * against the suite's schemas when the version is saved.
 */
import { useState, type FC } from 'react';
import { ConversationEditor, toConversation, toLines, type Line } from './ConversationEditor';
import { FieldLabel, ListField, fieldClass, isRecord, strings, toList, type ContentEditorProps } from './editorParts';
import { OllieChatContentEditor } from './OllieChatContentEditor';

export type { CaseInputExpect } from './editorParts';

const JsonContentEditor = ({ initial, disabled, onChange }: ContentEditorProps) => {
  const [input, setInput] = useState(() => JSON.stringify(initial?.input ?? {}, null, 2));
  const [expect, setExpect] = useState(() => JSON.stringify(initial?.expect ?? {}, null, 2));
  const [problem, setProblem] = useState<string | null>(null);

  const update = (nextInput: string, nextExpect: string) => {
    setInput(nextInput);
    setExpect(nextExpect);
    try {
      onChange({ input: JSON.parse(nextInput), expect: JSON.parse(nextExpect) });
      setProblem(null);
    } catch (caught) {
      onChange(null);
      setProblem(caught instanceof Error ? caught.message : 'Not valid JSON.');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <label>
        <FieldLabel title="Input" hint="JSON" />
        <textarea value={input} disabled={disabled} rows={20} onChange={(event) => update(event.target.value, expect)} className={`${fieldClass} font-mono text-xs`} />
      </label>
      <label>
        <FieldLabel title="Expected" hint="JSON" />
        <textarea value={expect} disabled={disabled} rows={20} onChange={(event) => update(input, event.target.value)} className={`${fieldClass} font-mono text-xs`} />
      </label>
      {problem && <p className="text-sm text-red-700 lg:col-span-2">{problem}</p>}
    </div>
  );
};

/** summarize-chat: `{ conversation }` and `{ mustInclude, mustKeepRisk?, mustNotInclude? }`. */
const SummaryContentEditor = ({ initial, disabled, onChange }: ContentEditorProps) => {
  const expect = isRecord(initial?.expect) ? initial.expect : {};
  const [lines, setLines] = useState<Line[]>(() => toLines(initial?.input));
  const [lists, setLists] = useState(() => ({
    mustInclude: strings(expect.mustInclude).join('\n'),
    mustKeepRisk: strings(expect.mustKeepRisk).join('\n'),
    mustNotInclude: strings(expect.mustNotInclude).join('\n'),
  }));

  const update = (nextLines: Line[], nextLists: typeof lists) => {
    setLines(nextLines);
    setLists(nextLists);
    const risk = toList(nextLists.mustKeepRisk);
    const wrong = toList(nextLists.mustNotInclude);
    onChange({
      input: { conversation: toConversation(nextLines) },
      expect: { mustInclude: toList(nextLists.mustInclude), ...(risk.length > 0 && { mustKeepRisk: risk }), ...(wrong.length > 0 && { mustNotInclude: wrong }) },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
      <ConversationEditor lines={lines} hint="Starts with the user. Tool items are kept as they are." disabled={disabled} onChange={(next) => update(next, lists)} />

      <div className="space-y-4">
        <p className="text-xs text-gray-600">One item per line, and one fact per item: the judge fails an item when any part of it is missing.</p>
        <ListField title="Must include" hint="What Ollie needs to know in a later chat" value={lists.mustInclude} disabled={disabled} onChange={(mustInclude) => update(lines, { ...lists, mustInclude })} />
        <ListField title="Must keep (risk)" hint="Suicidal thoughts, self-harm. Optional" value={lists.mustKeepRisk} disabled={disabled} onChange={(mustKeepRisk) => update(lines, { ...lists, mustKeepRisk })} />
        <ListField title="Must not say" hint="Wrong statements. Optional" value={lists.mustNotInclude} disabled={disabled} onChange={(mustNotInclude) => update(lines, { ...lists, mustNotInclude })} />
      </div>
    </div>
  );
};

const EDITORS: Record<string, FC<ContentEditorProps>> = {
  'summarize-chat': SummaryContentEditor,
  'ollie-chat': OllieChatContentEditor,
};

/** Has a form for the suite, else the JSON editor. */
export const CaseContentEditor = ({ suiteId, ...props }: ContentEditorProps & { suiteId: string }) => {
  const Editor = EDITORS[suiteId] ?? JsonContentEditor;
  return <Editor {...props} />;
};
